import React, { useState, createContext, useContext } from 'react';
type Language = 'it' | 'en';
const LANG_STORAGE_KEY = 'eiis.language';
/**
 * TODO (Backend): The user's language choice (selected on the first onboarding screen)
 * should be persisted to their profile and used server-side to determine the language
 * of ALL user-facing content: emails, push notifications, in-app copy, API responses, etc.
 * The frontend currently handles this via React context, but the backend must mirror
 * this preference so that every touchpoint respects the user's chosen language.
 */
interface Translations {
  [key: string]: {
    it: string;
    en: string;
  };
}
const translations: Translations = {
  // Nav / page titles
  'nav.schedule': {
    it: 'Programma',
    en: 'Schedule'
  },
  'nav.impact': {
    it: 'Impatto',
    en: 'Impact'
  },
  'nav.connect': {
    it: 'Connessioni',
    en: 'Connect'
  },
  'nav.profile': {
    it: 'Profilo',
    en: 'Profile'
  },
  // Screen 1 (OnboardingSprout / Welcome)
  'welcome.heading': {
    it: "Benvenuto nell'edizione 2026 del",
    en: 'Welcome to the 2026 edition of the'
  },
  'welcome.subheading': {
    it: 'European Innovation for Sustainability Summit',
    en: 'European Innovation for Sustainability Summit'
  },
  'welcome.cta': {
    it: 'Cominciamo',
    en: "Let's start"
  },
  'welcome.countdown.days': {
    it: "Mancano {n} giorni all'apertura",
    en: 'Opens in {n} days'
  },
  'welcome.countdown.oneDay': {
    it: "Manca 1 giorno all'apertura",
    en: 'Opens tomorrow'
  },
  'welcome.countdown.today': {
    it: 'Il Summit apre oggi',
    en: 'The Summit opens today'
  },
  'welcome.countdown.live': {
    it: 'Summit in corso',
    en: 'Summit is live'
  },
  // Screen 2 (OnboardingReveal) — S.02 Obiettivo app
  'intro.heading': {
    it: "Quest'anno ogni connessione conta. Davvero.",
    en: 'This year, every connection counts. For real.'
  },
  'intro.subtitle': {
    it: 'EIIS e il Comune di Roma pianteranno alberi veri in base alle connessioni che farai al Summit.',
    en: 'EIIS and the City of Rome will plant real trees based on the connections you make at the Summit.'
  },
  'intro.body': {
    it: "Con quest'app potrai consultare il programma completo del Summit, ma c'è molto di più.",
    en: "You can browse the full Summit programme here — but there's more to it."
  },
  'intro.description': {
    it: 'Scopri come le tue connessioni al Summit potranno generare un impatto concreto sulla città di Roma.',
    en: 'Discover how your connections at the Summit can generate a real impact on the city of Rome.'
  },
  'intro.cta': {
    it: "Cosa c'è di nuovo?",
    en: "What's new?"
  },
  // Screen 3 (OnboardingTechReveal) — S.03 Connessioni → Alberi
  'trees.heading': {
    it: 'Scegli di cosa vuoi parlare.',
    en: 'Pick what you want to talk about.'
  },
  'trees.subtitle': {
    it: 'Seleziona un argomento e il nostro algoritmo AI troverà chi condivide i tuoi stessi interessi.',
    en: 'Select a topic and our AI algorithm will find people who share your interests.'
  },
  'trees.body': {
    it: 'Le connessioni che stabilirete contribuiranno a piantare nuovi alberi in collaborazione con il Comune di Roma.',
    en: 'Every connection you make will help plant new trees, in partnership with the City of Rome.'
  },
  'trees.cta': {
    it: 'E poi?',
    en: 'And then?'
  },
  // Screen 4 (OnboardingBlindMatch) — S.04 Matching con AI
  'matching.caption': {
    it: '5 connessioni = 1 albero',
    en: '5 connections = 1 tree'
  },
  'matching.heading': {
    it: 'Incontra qualcuno che non ti aspetti.',
    en: "Meet someone you wouldn't expect."
  },
  'matching.subtitle': {
    it: "L'AI ti proporrà profili compatibili, ma non saprai chi sono finché non accetti. Niente bias, solo affinità.",
    en: "AI will suggest compatible profiles, but you won't know who they are until you accept. No bias, just affinity."
  },
  'matching.body': {
    it: 'Ogni incontro confermato è una connessione. Con 5 connessioni, EIIS pianterà un albero con il Comune di Roma.',
    en: 'Each confirmed meeting counts as one connection. When you reach 5, EIIS will plant a tree with the City of Rome.'
  },
  'matching.cta': {
    it: 'Come partecipo?',
    en: 'How do I join?'
  },
  // Screen 5 (OnboardingTransition) — S.05 Consensi
  'consent.heading': {
    it: 'Bastano 3 minuti del tuo tempo per conoscerti meglio e migliorare la qualità dei match che ti proporremo.',
    en: 'Just 3 minutes to tell us a bit about you and get better-quality matches.'
  },
  'consent.body': {
    it: 'Potrai completare il tuo profilo in qualsiasi momento.',
    en: 'You can finish your profile whenever you want.'
  },
  'consent.privacyLabel': {
    it: "Ho letto l'{link} e acconsento al trattamento dei miei dati personali per partecipare al matching EIIS Summit.",
    en: "I've read the {link} and I consent to the processing of my personal data to take part in EIIS Summit matching."
  },
  'consent.privacyLink': {
    it: 'informativa privacy',
    en: 'privacy notice'
  },
  'consent.aiLabel': {
    it: "Acconsento all'elaborazione AI dei contenuti che fornirò (testo, voce, file) per generare la mia project card e i match.",
    en: 'I consent to the AI processing of the content I share (text, voice, files) to generate my project card and matches.'
  },
  'consent.reassurance': {
    it: "I tuoi dati restano nell'app e vengono usati solo per il Summit. Nessuna condivisione esterna.",
    en: 'Your data stays inside the app and is only used for the Summit. No external sharing.'
  },
  'consent.cta': {
    it: 'Ci sto',
    en: "I'm in"
  },
  'consent.skip': {
    it: 'Salta per ora',
    en: 'Skip for now'
  },
  'consent.error.missing': {
    it: 'Per continuare conferma entrambi i consensi.',
    en: 'Please confirm both consents to continue.'
  },
  'privacy.title': {
    it: 'Informativa Privacy',
    en: 'Privacy Notice'
  },
  'privacy.placeholder': {
    it: "Questo è un testo segnaposto per l'informativa privacy. In produzione, qui andrà il testo legale completo o un iframe/link al documento ufficiale.",
    en: 'This is a placeholder for the privacy notice. In production, the full legal text or a link to the official document will be displayed here.'
  },
  // Prompt 13 — Projects wizard (3/4) refined
  'projects.heading': {
    it: 'Raccontaci il tuo progetto o la tua azienda',
    en: 'Tell us about your project or company'
  },
  'projects.subheading': {
    it: "Carica un company profile, una presentazione di progetto, una one-pager — oppure raccontacelo a voce o per iscritto. L'AI lo trasformerà in una scheda per trovare match migliori.",
    en: 'Upload a company profile, a project deck, a one-pager — or tell us by voice or in writing. The AI will turn it into a card to find better matches.'
  },
  'projects.upload.hint': {
    it: 'PPTX, PDF, DOCX, MD, TXT · max 15 MB',
    en: 'PPTX, PDF, DOCX, MD, TXT · max 15 MB'
  },
  'projects.tab.upload': {
    it: 'Carica',
    en: 'Upload'
  },
  'projects.tab.voice': {
    it: 'Voce',
    en: 'Voice'
  },
  'projects.tab.write': {
    it: 'Scrivi',
    en: 'Write'
  },
  'projects.ai.analyzing': {
    it: 'Analisi in corso…',
    en: 'Analysing…'
  },
  'projects.ai.cardReady': {
    it: 'Scheda generata — due dettagli e sei a posto',
    en: "Card ready — two details and you're set"
  },
  'projects.followup.custom': {
    it: 'Oppure scrivi una risposta tua…',
    en: 'Or write your own…'
  },
  'projects.followup.q1': {
    it: 'In che fase è il progetto?',
    en: 'What stage is your project in?'
  },
  'projects.followup.q1.a1': {
    it: 'Idea',
    en: 'Idea'
  },
  'projects.followup.q1.a2': {
    it: 'Prototipo',
    en: 'Prototype'
  },
  'projects.followup.q1.a3': {
    it: 'Early traction',
    en: 'Early traction'
  },
  'projects.followup.q1.a4': {
    it: 'Scaling',
    en: 'Scaling'
  },
  'projects.followup.q2': {
    it: 'Cosa cerchi al Summit?',
    en: 'What are you looking for at the Summit?'
  },
  'projects.followup.q2.a1': {
    it: 'Partner tecnologici',
    en: 'Tech partners'
  },
  'projects.followup.q2.a2': {
    it: 'Capitale / Investitori',
    en: 'Capital / Investors'
  },
  'projects.followup.q2.a3': {
    it: 'Policy & regolatori',
    en: 'Policy & regulators'
  },
  'projects.followup.q2.a4': {
    it: 'Feedback & confronto',
    en: 'Feedback & peer exchange'
  },
  // Screen 6 (OnboardingStart)
  'start.title': {
    it: 'Il tuo germoglio è pronto',
    en: 'Your sprout is ready'
  },
  'start.body': {
    it: 'Raccontaci qualcosa di te per farlo crescere. Ci vorranno solo 3 minuti.',
    en: 'Tell us something about yourself to help it grow. It will only take 3 minutes.'
  },
  'start.cta': {
    it: 'Tieni premuto',
    en: 'Hold to continue'
  },
  // Screen 7 (OnboardingRole)
  'role.progress': {
    it: '1 di 4',
    en: '1 of 4'
  },
  'role.heading': {
    it: 'Di cosa ti occupi?',
    en: 'What do you do?'
  },
  'role.subtext': {
    it: 'Queste info aiutano il match a funzionare meglio.',
    en: 'This info helps the match work better.'
  },
  'role.roleLabel': {
    it: 'Il tuo ruolo',
    en: 'Your role'
  },
  'role.rolePlaceholder': {
    it: 'Seleziona il tuo ruolo',
    en: 'Select your role'
  },
  'role.otherPlaceholder': {
    it: 'Specifica il tuo ruolo...',
    en: 'Specify your role...'
  },
  'role.orgLabel': {
    it: 'Organizzazione',
    en: 'Organization'
  },
  'role.orgOptional': {
    it: 'opzionale',
    en: 'optional'
  },
  'role.orgPlaceholder': {
    it: 'Nome dell\u2019azienda o ente',
    en: 'Company or organization name'
  },
  'role.sectorLabel': {
    it: 'Settore',
    en: 'Sector'
  },
  'role.sectorPlaceholder': {
    it: 'In che settore operi?',
    en: 'What sector do you work in?'
  },
  'role.sectorOtherPlaceholder': {
    it: 'Scrivi il tuo settore',
    en: 'Type your sector'
  },
  'role.cta': {
    it: 'Continua',
    en: 'Continue'
  },
  'role.skip': {
    it: 'Salta per ora',
    en: 'Skip for now'
  },
  // Roles
  'role.ceoFounder': {
    it: 'CEO / Fondatore',
    en: 'CEO / Founder'
  },
  'role.cLevel': {
    it: 'C-Level / Dirigente',
    en: 'C-Level Executive'
  },
  'role.directorManager': {
    it: 'Direttore / Manager',
    en: 'Director / Manager'
  },
  'role.investor': {
    it: 'Investitore',
    en: 'Investor'
  },
  'role.policyMaker': {
    it: 'Policy Maker',
    en: 'Policy Maker'
  },
  'role.researcherAcademic': {
    it: 'Ricercatore / Accademico',
    en: 'Researcher / Academic'
  },
  'role.other': {
    it: 'Altro...',
    en: 'Other...'
  },
  // Sectors
  'sector.energyEnvironment': {
    it: 'Energia e Ambiente',
    en: 'Energy & Environment'
  },
  'sector.financeInsurance': {
    it: 'Finanza e Assicurazioni',
    en: 'Finance & Insurance'
  },
  'sector.technologyDigital': {
    it: 'Tecnologia e Digitale',
    en: 'Technology & Digital'
  },
  'sector.foodAgriculture': {
    it: 'Agroalimentare',
    en: 'Food & Agriculture'
  },
  'sector.healthcareLifeSciences': {
    it: 'Salute e Life Sciences',
    en: 'Healthcare & Life Sciences'
  },
  'sector.infrastructureRealEstate': {
    it: 'Infrastrutture e Real Estate',
    en: 'Infrastructure & Real Estate'
  },
  'sector.manufacturingIndustry': {
    it: 'Manifattura e Industria',
    en: 'Manufacturing & Industry'
  },
  'sector.consultingServices': {
    it: 'Consulenza e Servizi',
    en: 'Consulting & Professional Services'
  },
  'sector.publicSectorPolicy': {
    it: 'Pubblica Amministrazione e Policy',
    en: 'Public Sector & Policy'
  },
  'sector.educationResearch': {
    it: 'Istruzione e Ricerca',
    en: 'Education & Research'
  },
  'sector.nonprofitNgos': {
    it: 'Non-profit e Terzo Settore',
    en: 'Nonprofit & NGOs'
  },
  'sector.transportLogistics': {
    it: 'Trasporti e Logistica',
    en: 'Transport & Logistics'
  },
  'sector.mediaCommunications': {
    it: 'Media e Comunicazione',
    en: 'Media & Communications'
  },
  'sector.other': {
    it: 'Altro...',
    en: 'Other...'
  },
  // Screen 8 (OnboardingThemes)
  'themes.progress': {
    it: '2 di 4',
    en: '2 of 4'
  },
  'themes.maxReached': {
    it: 'Hai raggiunto il massimo (5)',
    en: "You've reached the max (5)"
  },
  'themes.heading': {
    it: 'Cosa ti interessa?',
    en: 'What interests you?'
  },
  'themes.subtext': {
    it: 'Scegli i temi su cui vorresti confrontarti al Summit.',
    en: "Pick the topics you'd like to explore at the Summit."
  },
  'themes.cta': {
    it: 'Continua',
    en: 'Continue'
  },
  'themes.skip': {
    it: 'Salta per ora',
    en: 'Skip for now'
  },
  // Screen 9 (OnboardingProjects)
  'project.progress': {
    it: '3 di 4',
    en: '3 of 4'
  },
  'project.uploadTab': {
    it: 'Upload',
    en: 'Upload'
  },
  'project.uploadPrompt': {
    it: 'Trascina un file o tocca per selezionarlo',
    en: 'Drop a file or tap to select'
  },
  'project.uploadHint': {
    it: 'PPTX, PDF, DOCX, MD, TXT \u00b7 max 15 MB',
    en: 'PPTX, PDF, DOCX, MD, TXT \u00b7 max 15 MB'
  },
  'project.regenerate': {
    it: 'Rigenera',
    en: 'Regenerate'
  },
  'project.heading': {
    it: 'Hai un progetto da raccontare?',
    en: 'Got a project to share?'
  },
  'project.subtext': {
    it: "L'AI lo trasformerà in una scheda per trovare match ancora migliori.",
    en: 'Our AI will turn it into a card to find you even better matches.'
  },
  'project.voiceTab': {
    it: 'Voce',
    en: 'Voice'
  },
  'project.textTab': {
    it: 'Testo',
    en: 'Text'
  },
  'project.tapToRecord': {
    it: 'Tieni premuto per registrare',
    en: 'Press and hold to record'
  },
  'project.maxSeconds': {
    it: 'Max 60 secondi',
    en: 'Max 60 seconds'
  },
  'project.tapToStop': {
    it: 'Rilascia per terminare',
    en: 'Release to stop'
  },
  'project.analyzing': {
    it: "L'AI sta analizzando...",
    en: 'AI is analyzing...'
  },
  'project.creating': {
    it: 'Creazione scheda in corso',
    en: 'Creating card'
  },
  'project.generated': {
    it: "Generato dall'AI",
    en: 'AI generated'
  },
  'project.addAnother': {
    it: 'Aggiungi un altro progetto',
    en: 'Add another project'
  },
  'project.placeholder': {
    it: 'Descrivi brevemente il tuo progetto, sfida o idea...',
    en: 'Briefly describe your project, challenge or idea...'
  },
  'project.charCount': {
    it: 'caratteri',
    en: 'characters'
  },
  'project.minChars': {
    it: 'Minimo 10 caratteri',
    en: 'Minimum 10 characters'
  },
  'project.generate': {
    it: 'Genera con AI',
    en: 'Generate with AI'
  },
  'project.cta': {
    it: 'Continua',
    en: 'Continue'
  },
  'project.skip': {
    it: 'Salta per ora',
    en: 'Skip for now'
  },
  // Screen 10 (OnboardingRecap)
  'recap.progress': {
    it: '4 di 4',
    en: '4 of 4'
  },
  'recap.emailLabel': {
    it: 'Email',
    en: 'Email'
  },
  'recap.nameLabel': {
    it: 'Nome e cognome (opzionale)',
    en: 'Name (optional)'
  },
  'recap.firstNamePh': {
    it: 'Nome',
    en: 'First name'
  },
  'recap.lastNamePh': {
    it: 'Cognome',
    en: 'Last name'
  },
  'recap.addProjectCallout': {
    it: 'Aggiungi un progetto per migliorare i match',
    en: 'Add a project to improve your matches'
  },
  'recap.heading': {
    it: 'Sei dentro.',
    en: "You're in."
  },
  'recap.subtext': {
    it: 'Ecco il tuo profilo. Puoi modificarlo quando vuoi.',
    en: 'Here\u2019s your profile. You can edit it anytime.'
  },
  'recap.roleLabel': {
    it: 'Ruolo',
    en: 'Role'
  },
  'recap.themesLabel': {
    it: 'Temi di interesse',
    en: 'Topics of interest'
  },
  'recap.projectLabel': {
    it: 'Progetto',
    en: 'Project'
  },
  'recap.addProject': {
    it: '+ Aggiungi un progetto',
    en: '+ Add a project'
  },
  'recap.aiGenerated': {
    it: '✨ AI',
    en: '✨ AI'
  },
  'recap.seedPlanted': {
    it: '🌱 Il tuo seme è stato piantato',
    en: '🌱 Your seed has been planted'
  },
  'recap.connections': {
    it: '0 / 5 connessioni per il tuo albero',
    en: '0 / 5 connections for your tree'
  },
  'recap.cta': {
    it: 'Entra nel Summit',
    en: 'Enter the Summit'
  },
  // Skip nudge sheet
  'nudge.heading': {
    it: '⏱️ Ci vogliono meno di 3 minuti',
    en: '⏱️ It takes less than 3 minutes'
  },
  'nudge.body': {
    it: 'Più cose ci racconti, migliori saranno i match che riceverai al Summit. Ma puoi sempre completare dopo dal tuo profilo.',
    en: 'The more you share, the better your matches will be at the Summit. But you can always finish later from your profile.'
  },
  'nudge.continue': {
    it: 'Va bene, continuo',
    en: "Alright, I'll continue"
  },
  'nudge.later': {
    it: 'Faccio dopo',
    en: "I'll do it later"
  },
  // Cookie consent banner
  'cookie.title': {
    it: 'Cookie',
    en: 'Cookies'
  },
  'cookie.body': {
    it: "Usiamo cookie tecnici per far funzionare l'app. Con il tuo consenso usiamo anche cookie di analytics per capire come migliorarla. Nessun cookie di marketing o profilazione.",
    en: 'We use essential cookies to make the app work. With your consent, we also use analytics cookies to understand how to improve it. No marketing or profiling cookies.'
  },
  'cookie.acceptAll': {
    it: 'Accetta tutti',
    en: 'Accept all'
  },
  'cookie.rejectNonEssential': {
    it: 'Rifiuta non essenziali',
    en: 'Reject non-essential'
  },
  'cookie.customise': {
    it: 'Personalizza',
    en: 'Customise'
  },
  // Accessibility labels (a11y)
  'a11y.switchLanguage': {
    it: 'Cambia lingua',
    en: 'Switch language'
  },
  'a11y.uploadPhoto': {
    it: 'Carica foto profilo',
    en: 'Upload profile photo'
  },
  'a11y.editCard': {
    it: 'Modifica scheda',
    en: 'Edit card'
  },
  'a11y.regenerate': {
    it: 'Rigenera scheda AI',
    en: 'Regenerate AI card'
  },
  'a11y.clearInput': {
    it: 'Cancella',
    en: 'Clear'
  },
  // Common
  'common.back': {
    it: 'Indietro',
    en: 'Back'
  },
  'common.close': {
    it: 'Chiudi',
    en: 'Close'
  },
  // Match / Discover tab
  'match.tab.discover': {
    it: 'Scopri',
    en: 'Discover'
  },
  'match.tab.requests': {
    it: 'Richieste ({n})',
    en: 'Requests ({n})'
  },
  'match.filter': {
    it: 'Filtra',
    en: 'Filter'
  },
  'match.filter.title': {
    it: 'Filtra per tema',
    en: 'Filter by topic'
  },
  'match.filter.close': {
    it: 'Chiudi',
    en: 'Close'
  },
  'match.filter.showAll': {
    it: 'Mostra tutti',
    en: 'Show all'
  },
  'match.filter.apply': {
    it: 'Applica',
    en: 'Apply'
  },
  // Discover list page
  'discover.title': {
    it: 'Scopri',
    en: 'Discover'
  },
  'discover.subtitle': {
    it: 'Esplora i temi e trova chi ne parla',
    en: "Browse topics and meet who's talking about them"
  },
  'discover.suggested': {
    it: 'Suggeriti per te',
    en: 'Recommended for you'
  },
  'discover.yourInterests': {
    it: 'I tuoi interessi',
    en: 'Your interests'
  },
  'discover.otherTopics': {
    it: 'Altri temi',
    en: 'Other topics'
  },
  'discover.all': {
    it: 'Tutti i temi',
    en: 'All topics'
  },
  'discover.allAlone': {
    it: 'Esplora i temi',
    en: 'Explore topics'
  },
  'discover.interestedCount': {
    it: '{n} persone ne parlano',
    en: '{n} people are talking about it'
  },
  // Topic detail — shared
  'topic.keyQuestions': {
    it: 'Domande chiave',
    en: 'Key questions'
  },
  'topic.people': {
    it: 'Persone interessate',
    en: 'People interested'
  },
  'topic.match': {
    it: 'match',
    en: 'match'
  },
  'topic.summitSessions': {
    it: 'Interventi al Summit',
    en: 'Summit sessions'
  },
  'topic.noSessions': {
    it: 'Nessun intervento specifico in programma.',
    en: 'No specific sessions scheduled.'
  },
  // Profile detail (blurred identity + connect)
  'profile.connect': {
    it: 'Match e proponi un orario',
    en: 'Match & propose a time'
  },
  'profile.matchAndPropose': {
    it: 'Match e proponi un orario',
    en: 'Match & propose a time'
  },
  'profile.meetCta': {
    it: 'Incontra',
    en: 'Meet'
  },
  'profile.showQr': {
    it: 'Mostra QR per connetterti',
    en: 'Show QR to connect'
  },
  'profile.qrTitle': {
    it: 'Scansiona per connetterti',
    en: 'Scan to connect'
  },
  'profile.qrSubtitle': {
    it: "Fai scansionare questo QR all'altra persona al Summit: trasformerà il match in una connessione confermata.",
    en: 'Let the other person scan this QR at the Summit — it turns your match into a confirmed connection.'
  },
  'profile.qrHint': {
    it: 'Il codice è valido solo per questo match.',
    en: 'This code is only valid for this match.'
  },
  'profile.qrWaiting': {
    it: 'In attesa di scansione…',
    en: 'Waiting to be scanned…'
  },
  'profile.qrScanned': {
    it: 'Scansionato!',
    en: 'Scanned!'
  },
  'profile.qrScannedBody': {
    it: 'Conferma per trasformare il match in connessione.',
    en: 'Confirm to turn this match into a connection.'
  },
  'profile.confirmConnection': {
    it: 'Conferma connessione',
    en: 'Confirm connection'
  },
  'profile.connectionConfirmed': {
    it: 'Connessione confermata!',
    en: 'Connection confirmed!'
  },
  'profile.connectionsProgress': {
    it: '{n} / 5 connessioni per il tuo albero',
    en: '{n} / 5 connections for your tree'
  },
  'profile.done': {
    it: 'Fatto',
    en: 'Done'
  },
  'common.cancel': {
    it: 'Annulla',
    en: 'Cancel'
  },
  'tree.planted': {
    it: '🌳 Hai piantato un albero!',
    en: "🌳 You've planted a tree!"
  },
  'profile.requestSent': {
    it: 'Richiesta inviata',
    en: 'Request sent'
  },
  'profile.alreadyConnected': {
    it: 'Già connessi',
    en: 'Already connected'
  },
  'profile.topics': {
    it: 'Temi di interesse',
    en: 'Topics of interest'
  },
  'profile.blurNotice': {
    it: "L'identità resta nascosta finché entrambi non accettate l'incontro.",
    en: 'Identity stays hidden until you both accept the meeting.'
  },
  // Propose time sheet
  'meeting.proposeTitle': {
    it: 'Proponi un orario',
    en: 'Propose a time'
  },
  'meeting.proposeSubtitle': {
    it: 'Scegli quando vorresti incontrarvi al Summit.',
    en: "Pick when you'd like to meet at the Summit."
  },
  'meeting.proposeAltTitle': {
    it: 'Proponi un altro orario',
    en: 'Propose another time'
  },
  'meeting.rescheduleTitle': {
    it: "Modifica l'orario",
    en: 'Change the time'
  },
  'meeting.send': {
    it: 'Invia richiesta',
    en: 'Send request'
  },
  'meeting.sendAlternative': {
    it: 'Invia alternativa',
    en: 'Send alternative'
  },
  'meeting.sentGeneric': {
    it: 'Richiesta inviata',
    en: 'Request sent'
  },
  'meeting.sentConfirmation': {
    it: 'Richiesta inviata per {slot}',
    en: 'Request sent for {slot}'
  },
  'meeting.day.may8': {
    it: 'Ven 8 maggio',
    en: 'Fri, May 8'
  },
  'meeting.day.may9': {
    it: 'Sab 9 maggio',
    en: 'Sat, May 9'
  },
  'meeting.time.morning': {
    it: 'Mattina · 09:00 – 13:00',
    en: 'Morning · 9:00 AM – 1:00 PM'
  },
  'meeting.time.afternoon': {
    it: 'Pomeriggio · 14:00 – 18:00',
    en: 'Afternoon · 2:00 – 6:00 PM'
  },
  // Connections page
  'connections.title': {
    it: 'I tuoi match',
    en: 'Your matches'
  },
  'connections.subtitle': {
    it: '{n} persone da incontrare',
    en: '{n} people to meet'
  },
  'connections.subtitleWithIncoming': {
    it: '{n} nuove richieste in attesa',
    en: '{n} new requests waiting'
  },
  'connections.pendingHeader': {
    it: 'Richieste in arrivo',
    en: 'Incoming requests'
  },
  'connections.confirmedHeader': {
    it: 'Incontri confermati',
    en: 'Confirmed meetings'
  },
  'connections.sentHeader': {
    it: 'Richieste inviate',
    en: 'Sent requests'
  },
  'connections.wantsToMeet': {
    it: 'Ti ha invitato',
    en: 'Invited you'
  },
  'connections.accept': {
    it: 'Accetta',
    en: 'Accept'
  },
  'connections.proposeAlt': {
    it: 'Proponi altro',
    en: 'Propose other'
  },
  'connections.meet': {
    it: 'Incontra',
    en: 'Meet'
  },
  'connections.waiting': {
    it: 'In attesa',
    en: 'Waiting'
  },
  'connections.empty': {
    it: 'Non hai ancora match. Esplora i temi per iniziare.',
    en: 'No matches yet. Browse topics to get started.'
  },
  // Topic: AI Governance
  'topic.aiGovernance.name': {
    it: "Governance dell'AI",
    en: 'AI Governance'
  },
  'topic.aiGovernance.desc': {
    it: 'Progettare framework etici, garantire trasparenza e responsabilità nei sistemi AI che influenzano politiche e società.',
    en: 'Designing ethical frameworks, ensuring transparency and accountability in AI systems that shape policy and society.'
  },
  'topic.aiGovernance.descLong': {
    it: "Come costruiamo un'AI che sia utile e sicura allo stesso tempo? Dalle regole europee ai framework aziendali, parliamo di chi decide, chi risponde e come bilanciare velocità e supervisione.",
    en: 'How do we build AI that is both useful and safe? From EU rules to corporate frameworks, we explore who decides, who is accountable, and how to balance speed with oversight.'
  },
  'topic.aiGovernance.q1': {
    it: 'Come bilanci innovazione e supervisione umana?',
    en: 'How do you balance innovation with human oversight?'
  },
  'topic.aiGovernance.q2': {
    it: 'Quale framework regolatorio vedi più applicabile al tuo settore?',
    en: 'Which regulatory framework fits your sector best?'
  },
  'topic.aiGovernance.q3': {
    it: "Dove si colloca la responsabilità quando l'AI sbaglia?",
    en: 'Where does accountability sit when AI fails?'
  },
  // Topic: Climate Adaptation
  'topic.climateAdaptation.name': {
    it: 'Adattamento Climatico',
    en: 'Climate Adaptation'
  },
  'topic.climateAdaptation.desc': {
    it: 'Costruire strategie di resilienza per comunità e città che affrontano la crisi climatica, dal livello dei mari agli eventi estremi.',
    en: 'Building resilience strategies for communities and cities facing climate disruption, from rising seas to extreme weather.'
  },
  'topic.climateAdaptation.descLong': {
    it: 'La mitigazione non basta più: il clima è già cambiato. Parliamo di come rendere città, infrastrutture e filiere resilienti a ondate di calore, siccità e alluvioni.',
    en: 'Mitigation alone is no longer enough — the climate has already shifted. This is about making cities, infrastructure and supply chains resilient to heatwaves, droughts and floods.'
  },
  'topic.climateAdaptation.q1': {
    it: 'Qual è il rischio climatico più sottovalutato nel tuo settore?',
    en: 'Which climate risk is most underestimated in your sector?'
  },
  'topic.climateAdaptation.q2': {
    it: "Come misuri il ritorno economico dell'adattamento?",
    en: 'How do you measure the economic return of adaptation?'
  },
  'topic.climateAdaptation.q3': {
    it: 'Chi paga per le infrastrutture resilienti?',
    en: 'Who pays for resilient infrastructure?'
  },
  // Topic: Food Security
  'topic.foodSecurity.name': {
    it: 'Sicurezza Alimentare',
    en: 'Food Security'
  },
  'topic.foodSecurity.desc': {
    it: "Ripensare i sistemi agroalimentari dal campo alla tavola: filiere sostenibili, gestione delle risorse e innovazione per l'accesso al cibo.",
    en: 'Rethinking agrifood systems from farm to fork: sustainable supply chains, resource management and innovation for food access.'
  },
  'topic.foodSecurity.descLong': {
    it: 'Sistemi alimentari più resilienti, equi e sostenibili. Dal campo alla tavola: come garantiamo accesso al cibo mentre affrontiamo crisi climatiche e geopolitiche?',
    en: 'More resilient, fair and sustainable food systems. From farm to table: how do we secure access to food while navigating climate and geopolitical shocks?'
  },
  'topic.foodSecurity.q1': {
    it: 'Qual è la leva più sottovalutata per la sicurezza alimentare?',
    en: 'Which lever for food security is most overlooked?'
  },
  'topic.foodSecurity.q2': {
    it: 'Come riduci la dipendenza da filiere globali fragili?',
    en: 'How do you reduce dependence on fragile global supply chains?'
  },
  'topic.foodSecurity.q3': {
    it: 'Dove vedi più potenziale: produzione, distribuzione o consumo?',
    en: 'Where is the biggest lever — production, distribution or consumption?'
  },
  // Topic: Biodiversity
  'topic.biodiversity.name': {
    it: 'Biodiversità',
    en: 'Biodiversity'
  },
  'topic.biodiversity.desc': {
    it: "Proteggere gli ecosistemi, comprendere il valore delle specie e degli habitat, e gestire l'impatto delle attività umane sulla natura.",
    en: 'Protecting ecosystems, understanding the value of species and habitats, and managing the impact of human activities on nature.'
  },
  'topic.biodiversity.descLong': {
    it: 'La biodiversità è un servizio ecosistemico essenziale, non un lusso. Parliamo di come integrarla nelle decisioni di business, di policy e di investimento.',
    en: 'Biodiversity is essential infrastructure, not a luxury. We discuss how to bring it into business, policy and investment decisions.'
  },
  'topic.biodiversity.q1': {
    it: 'Come prezzi il valore della biodiversità nei tuoi progetti?',
    en: 'How do you price the value of biodiversity into your projects?'
  },
  'topic.biodiversity.q2': {
    it: 'Protezione o ripristino: dove concentriamo risorse?',
    en: 'Protection or restoration — where should we focus resources?'
  },
  'topic.biodiversity.q3': {
    it: 'Qual è il ruolo del settore privato?',
    en: 'What is the role of the private sector?'
  },
  // Topic: Innovative Finance
  'topic.innovativeFinance.name': {
    it: 'Finanza Innovativa',
    en: 'Innovative Finance'
  },
  'topic.innovativeFinance.desc': {
    it: 'Blended finance, impact investing e nuovi modelli di finanziamento per accelerare la transizione verde e sociale in Europa.',
    en: 'Blended finance, impact investing and new funding models to accelerate the green and social transition in Europe.'
  },
  'topic.innovativeFinance.descLong': {
    it: 'Green bonds, blended finance, impact investing, carbon markets: quali strumenti funzionano davvero e come scalarli? Un confronto tra finanza, policy e imprese.',
    en: 'Green bonds, blended finance, impact investing, carbon markets: which instruments really work and how do we scale them? A dialogue across finance, policy and business.'
  },
  'topic.innovativeFinance.q1': {
    it: 'Cosa rallenta oggi il capitale sulla transizione?',
    en: 'What is slowing capital flows into the transition today?'
  },
  'topic.innovativeFinance.q2': {
    it: 'Blended finance: hype o leva reale?',
    en: 'Blended finance: hype or real lever?'
  },
  'topic.innovativeFinance.q3': {
    it: 'Come misuri un impatto che sia credibile?',
    en: 'How do you measure impact in a way that is credible?'
  },
  // Topic: Twin Transition
  'topic.twinTransition.name': {
    it: 'Transizione Gemella',
    en: 'Twin Transition'
  },
  'topic.twinTransition.desc': {
    it: 'Integrare trasformazione digitale e transizione verde per una crescita sostenibile che non lasci indietro nessuna comunità.',
    en: 'Integrating digital transformation and green transition to drive sustainable growth without leaving communities behind.'
  },
  'topic.twinTransition.descLong': {
    it: 'Digitalizzazione e sostenibilità si rafforzano a vicenda. Ma dove sono i conflitti reali — consumi energetici, lock-in tecnologici, competenze — e come li superiamo?',
    en: 'Digital and sustainability reinforce each other — but real tensions exist around energy use, lock-in and skills. How do we resolve them?'
  },
  'topic.twinTransition.q1': {
    it: 'Dove digitale e sostenibilità entrano in conflitto?',
    en: 'Where do digital and sustainability goals conflict?'
  },
  'topic.twinTransition.q2': {
    it: "Qual è l'investimento tech che più sposta l'ago per la sostenibilità?",
    en: 'Which tech investment moves the needle most for sustainability?'
  },
  'topic.twinTransition.q3': {
    it: 'Chi guida questa trasformazione nella tua organizzazione?',
    en: 'Who leads this transformation in your organisation?'
  },
  // Topic: Frontier Tech
  'topic.frontierTech.name': {
    it: 'Tecnologie di Frontiera',
    en: 'Frontier Technologies'
  },
  'topic.frontierTech.desc': {
    it: 'Esplorare le tecnologie emergenti con il maggior potenziale per ridurre le emissioni e risolvere le sfide globali della sostenibilità.',
    en: 'Exploring emerging technologies with the highest potential to reduce emissions and solve global sustainability challenges.'
  },
  'topic.frontierTech.descLong': {
    it: 'Tecnologie di frontiera e sostenibilità: un confronto concreto su dove queste innovazioni possono davvero fare la differenza nei prossimi 5 anni.',
    en: 'Frontier technologies meet sustainability: a grounded conversation on where these innovations can genuinely move the dial over the next five years.'
  },
  'topic.frontierTech.q1': {
    it: 'Quale tecnologia di frontiera è più vicina al deployment reale?',
    en: 'Which frontier technology is closest to real deployment?'
  },
  'topic.frontierTech.q2': {
    it: 'Dove passa la linea tra ricerca e prodotto?',
    en: 'Where is the line between research and product?'
  },
  'topic.frontierTech.q3': {
    it: 'Come eviti di inseguire solo la prossima hype wave?',
    en: 'How do you avoid chasing only the next hype wave?'
  },
  // Topic: Regenerative
  'topic.regenerative.name': {
    it: 'Innovazione Rigenerativa',
    en: 'Regenerative Innovation'
  },
  'topic.regenerative.desc': {
    it: 'Andare oltre il fare meno danni: ripristinare attivamente gli ecosistemi e progettare soluzioni che rigenerano capitale sociale e naturale.',
    en: 'Going beyond doing less harm: actively restoring ecosystems and designing solutions that regenerate social and natural capital.'
  },
  'topic.regenerative.descLong': {
    it: "Business rigenerativi, agricoltura rigenerativa, design rigenerativo: un cambio di paradigma che passa dal 'ridurre i danni' al 'generare valore ecologico'.",
    en: "Regenerative business, agriculture and design: a shift from 'reduce harm' to 'actively create ecological value'."
  },
  'topic.regenerative.q1': {
    it: 'Cosa separa un progetto sostenibile da uno rigenerativo?',
    en: 'What separates a sustainable project from a regenerative one?'
  },
  'topic.regenerative.q2': {
    it: 'Come misuri esiti rigenerativi in modo credibile?',
    en: 'How do you measure regenerative outcomes credibly?'
  },
  'topic.regenerative.q3': {
    it: 'Qual è il primo caso reale che ti ha convinto?',
    en: 'Which first real-world case convinced you?'
  },
  // Topic: Partnerships
  'topic.partnerships.name': {
    it: 'Nuovi Modelli di Partnership',
    en: 'New Partnership Models'
  },
  'topic.partnerships.desc': {
    it: 'Collaborazione cross-settore tra organizzazioni, istituzioni, ONG e reti giovanili per co-creare soluzioni su larga scala.',
    en: 'Cross-sector collaboration between organizations, institutions, NGOs and youth networks to co-create solutions at scale.'
  },
  'topic.partnerships.descLong': {
    it: 'Pubblico, privato, non-profit, ricerca: le sfide della transizione richiedono alleanze nuove. Cosa funziona, cosa fallisce e perché?',
    en: 'Public, private, non-profit, research: transition challenges require new alliances. What works, what fails, and why?'
  },
  'topic.partnerships.q1': {
    it: 'Qual è la partnership più efficace che hai visto?',
    en: 'What is the most effective partnership you have seen?'
  },
  'topic.partnerships.q2': {
    it: 'Cosa fa fallire le collaborazioni cross-settore?',
    en: 'What makes cross-sector collaborations fail?'
  },
  'topic.partnerships.q3': {
    it: 'Come allinei tempi e incentivi tra attori diversi?',
    en: 'How do you align timelines and incentives across different actors?'
  },
  // Topic: Skills & Mobility
  'topic.skillsMobility.name': {
    it: 'Competenze e Mobilità dei Talenti',
    en: 'Skills & Talent Mobility'
  },
  'topic.skillsMobility.desc': {
    it: 'Sviluppare la leadership di nuova generazione e abilitare il movimento globale delle competenze per affrontare le transizioni della sostenibilità.',
    en: 'Developing next-generation leadership and enabling the global movement of skills to tackle the sustainability transitions.'
  },
  'topic.skillsMobility.descLong': {
    it: 'La transizione richiede profili nuovi e movimento di talenti tra settori. Parliamo di reskilling, attrazione, e di come le organizzazioni stanno ripensando il lavoro.',
    en: 'The transition needs new profiles and cross-sector talent mobility. We discuss reskilling, attraction and how organisations are rethinking work itself.'
  },
  'topic.skillsMobility.q1': {
    it: 'Quale competenza della transizione è più difficile da assumere?',
    en: 'Which transition skill is hardest to hire for?'
  },
  'topic.skillsMobility.q2': {
    it: 'Reskilling interno o acquisizione esterna: cosa funziona?',
    en: 'Internal reskilling or external hiring — what works?'
  },
  'topic.skillsMobility.q3': {
    it: 'Come trattieni talento scarso in un mercato competitivo?',
    en: 'How do you retain scarce talent in a competitive market?'
  },
  // Pre-event Hub (PreEventHub)
  'hub.eyebrow': {
    it: 'European Innovation for Sustainability Summit',
    en: 'European Innovation for Sustainability Summit'
  },
  'hub.daysLeft': {
    it: "giorni all'apertura",
    en: 'days to kickoff'
  },
  'hub.oneDay': {
    it: "giorno all'apertura",
    en: 'day to kickoff'
  },
  'hub.today': {
    it: 'È oggi',
    en: "It's today"
  },
  'hub.live': {
    it: 'Summit in corso',
    en: 'Summit is live'
  },
  'hub.dates': {
    it: '8–9 maggio 2026 · Roma',
    en: '8–9 May 2026 · Rome'
  },
  'hub.status.title': {
    it: 'La tua preparazione',
    en: 'Your prep'
  },
  'hub.status.allDone': {
    it: 'Tutto pronto, ci vediamo a Roma.',
    en: 'All set — see you in Rome.'
  },
  'hub.status.profile': {
    it: 'Profilo base',
    en: 'Basic profile'
  },
  'hub.status.interests': {
    it: 'Interessi selezionati',
    en: 'Interests selected'
  },
  'hub.status.project': {
    it: 'Progetto aggiunto',
    en: 'Project added'
  },
  'hub.action.editProfile': {
    it: 'Modifica profilo',
    en: 'Edit profile'
  },
  'hub.action.addProject': {
    it: 'Aggiungi progetto',
    en: 'Add project'
  },
  'hub.enterNow': {
    it: "Entra nell'app",
    en: 'Enter the app'
  },
  // TreeHome / Impact tab
  'impact.your': {
    it: 'Il tuo impatto',
    en: 'Your impact'
  },
  'impact.connections': {
    it: 'Connessioni',
    en: 'Connections'
  },
  'impact.community': {
    it: 'Impatto della community',
    en: 'Community impact'
  },
  'impact.trees': {
    it: 'Alberi',
    en: 'Trees'
  },
  'impact.participants': {
    it: 'Partecipanti',
    en: 'Participants'
  },
  'impact.forestCaption': {
    it: 'Insieme stiamo costruendo una foresta. Ogni connessione avvicina la community a un nuovo albero piantato a Roma.',
    en: "Together we're building a forest. Each connection brings the community closer to a new tree planted in Rome."
  },
  'impact.goToConnections': {
    it: 'Vai alle connessioni',
    en: 'Go to connections'
  },
  'impact.contributeToo': {
    it: 'Contribuisci anche tu',
    en: 'Contribute too'
  },
  'impact.scan': {
    it: 'Scansiona',
    en: 'Scan'
  },
  // TreeHome — new namespaced keys (tree.*)
  'tree.yourImpact': {
    it: 'Il tuo impatto',
    en: 'Your impact'
  },
  'tree.communityImpact': {
    it: 'Impatto della community',
    en: 'Community impact'
  },
  'tree.connections': {
    it: 'Connessioni',
    en: 'Connections'
  },
  'tree.trees': {
    it: 'Alberi',
    en: 'Trees'
  },
  'tree.participants': {
    it: 'Partecipanti',
    en: 'Participants'
  },
  'tree.communityBody': {
    it: 'Insieme stiamo costruendo una foresta. Ogni connessione avvicina la community a un nuovo albero piantato a Roma.',
    en: "Together we're building a forest. Each connection brings the community closer to a new tree planted in Rome."
  },
  'tree.cta.goToConnections': {
    it: 'Vai alle connessioni',
    en: 'Go to connections'
  },
  'tree.cta.contributeToo': {
    it: 'Contribuisci anche tu',
    en: 'Contribute too'
  },
  'tree.emptyState': {
    it: 'Il tuo seme è piantato. Fai il tuo primo match per iniziare a crescere.',
    en: 'Your seed is planted. Make your first match to start growing.'
  },
  'tree.scan': {
    it: 'Scansiona',
    en: 'Scan'
  },
  'tree.progressToFirst': {
    it: '{n} / 5 per il tuo primo albero',
    en: '{n} / 5 toward your first tree'
  },
  'tree.treePlantedOne': {
    it: '1 albero piantato',
    en: '1 tree planted'
  },
  'tree.treesPlantedMany': {
    it: '{n} alberi piantati',
    en: '{n} trees planted'
  },
  // SummitHome / Schedule
  'schedule.day8': {
    it: '8 Maggio',
    en: 'May 8'
  },
  'schedule.day9': {
    it: '9 Maggio',
    en: 'May 9'
  },
  'schedule.moderator': {
    it: 'Moderatore',
    en: 'Moderator'
  },
  'schedule.participants': {
    it: '{n} relatori',
    en: '{n} speakers'
  },
  'schedule.participant': {
    it: '{n} relatore',
    en: '{n} speaker'
  },
  'schedule.tbc': {
    it: 'Da confermare',
    en: 'To be confirmed'
  },
  'schedule.tbd': {
    it: 'Da definire',
    en: 'To be announced'
  },
  // OnboardingProjects — remaining hardcoded strings (2.9)
  'projects.recordAgain': {
    it: 'Registra di nuovo',
    en: 'Record again'
  },
  'projects.submitToAI': {
    it: "Invia all'AI",
    en: 'Submit to AI'
  },
  'projects.rewriteAI': {
    it: 'Riscrivi con AI',
    en: 'Rewrite with AI'
  },
  'projects.save': {
    it: 'Salva',
    en: 'Save'
  },
  'projects.playRecording': {
    it: 'Riproduci registrazione',
    en: 'Play recording'
  },
  'projects.editProject': {
    it: 'Modifica progetto',
    en: 'Edit project'
  },
  'projects.close': {
    it: 'Chiudi',
    en: 'Close'
  },
  'projects.title': {
    it: 'Titolo',
    en: 'Title'
  },
  'projects.description': {
    it: 'Descrizione',
    en: 'Description'
  },
  'projects.topics': {
    it: 'Temi',
    en: 'Topics'
  },
  'projects.addTopic': {
    it: 'Aggiungi tema',
    en: 'Add topic'
  },
  'projects.add': {
    it: 'Aggiungi',
    en: 'Add'
  },
  'projects.cancel': {
    it: 'Annulla',
    en: 'Cancel'
  },
  'projects.questionLabel': {
    it: 'Domanda {n}',
    en: 'Question {n}'
  },
  'projects.orAddOwn': {
    it: 'Oppure scrivi la tua',
    en: 'Or add your own'
  },
  'projects.next': {
    it: 'Avanti',
    en: 'Next'
  },
  'projects.back': {
    it: 'Indietro',
    en: 'Back'
  },
  'projects.submit': {
    it: 'Invia',
    en: 'Submit'
  },
  'projects.confirmDelete': {
    it: 'Confermi?',
    en: 'Confirm?'
  },
  'projects.deleteProject': {
    it: 'Elimina progetto',
    en: 'Delete project'
  },
  'projects.confirmDeleteAria': {
    it: 'Conferma eliminazione progetto',
    en: 'Confirm delete project'
  },
  'projects.answerToContinue': {
    it: 'Rispondi per continuare',
    en: 'Answer to continue'
  },
  'projects.answerMoreOne': {
    it: 'Rispondi ad altra {n} domanda',
    en: 'Answer {n} more question'
  },
  'projects.answerMoreMany': {
    it: 'Rispondi ad altre {n} domande',
    en: 'Answer {n} more questions'
  },
  'projects.generatingSummary': {
    it: 'Generazione del riassunto…',
    en: 'Generating your summary…'
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
function detectInitialLanguage(): Language {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === 'it' || stored === 'en') return stored;
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      if (navigator.language.toLowerCase().startsWith('it')) return 'it';
    }
  } catch {
    // fail silently — fall back to default
  }
  return 'en';
}
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return key in params ? String(params[key]) : match;
  });
}
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] = useState<Language>(() => detectInitialLanguage());
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(LANG_STORAGE_KEY, lang);
      }
    } catch {
      // fail silently
    }
  };
  const t = (key: string, params?: Record<string, string | number>): string => {
    if (!translations[key]) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return interpolate(translations[key][language], params);
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t
  }} data-id="element-154">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}